import React, { useCallback, useMemo } from "react";

import SignalEmptyState from "components/form_components/SignalEmptyState";
import {
  RELEVANCY_SEARCH_NEW_USERS,
  useVariableValue,
} from "lib/featureFlags/generated/dvcVariableTypes";
import Signal from "lib/icons/Signal";
import { simpleArrayDedupe } from "lib/utils";
import ClassificationListSelect, { Option } from "../ClassificationListSelect";
import Dropdown from "../Dropdown";
import { CpvCode, findMatchingCpvCodes, getCpvPrefix } from "./cpv_code_utils";
import CpvTreeSelect from "./CpvTreeSelect";
import { CpvTreeNode } from "./useCpvTree";

import css from "./CpvDropdown.module.scss";

type Props = {
  cpvTree: CpvTreeNode[];
  allCpvCodes?: CpvCode[];
  signalCpvCodes?: CpvCode[];
  isLoading: boolean;
  isOpen: boolean;
  textSearch: string;
  value: string[];
  signalCodeIds: string[];
  onChange: (newValue: string[]) => void;
};

function CpvDropdown({
  isOpen,
  textSearch,
  value,
  signalCodeIds,
  onChange,
  isLoading,
  cpvTree,
  allCpvCodes = [],
  signalCpvCodes = [],
}: Props) {
  const signalCpvcodeOptions = signalCpvCodes.map((cpv) => ({
    label: `${cpv.code}: ${cpv.name}`,
    value: cpv.code,
    icon: <Signal className={css.signalIcon} aria-label="signalIcon" />,
  }));
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const filteredCpvCodes = useMemo(() => {
    if (textSearch.length === 0 || !cpvTree.length) {
      return [];
    }
    return findMatchingCpvCodes(cpvTree, textSearch, textSearch.match(/^\d+$/) !== null);
  }, [textSearch, cpvTree]);

  const categoryToLabel = {
    signalCpvCodes: (
      <label className={css.label}>
        <Signal className={css.signalIcon} />
        CPV Code signals{` (${signalCodeIds.length})`}
      </label>
    ),
    browseCpvCodes: "Browse CPV Codes",
  };

  const isChecked = (option: Option): boolean => {
    if (value.includes(option.value)) {
      return true;
    }

    return !!value.find((v) => {
      const cpvPrefix = getCpvPrefix(v);
      return option.value.startsWith(cpvPrefix);
    });
  };
  // this will be used to update the selected CPV codes
  // when the user selects a CPV code from the search dropdown
  // it checks for any CPVs that have been deselected
  // before merging the selected CPV codes with the new ones
  const onFilteredChange = useCallback(
    (newCpvCodes: string[]) => {
      const removedCpvCodes: string[] = [];
      for (const code of value) {
        if (filteredCpvCodes.some((f) => f.value === code) && !newCpvCodes.includes(code)) {
          removedCpvCodes.push(code);
        }
      }

      const filteredSelectedCpvs = value.filter((v) => !removedCpvCodes.includes(v));
      const newSelection = simpleArrayDedupe([...filteredSelectedCpvs, ...newCpvCodes]);
      onChange(newSelection);
    },
    [filteredCpvCodes, onChange, value],
  );

  const categoryToContent = {
    signalCpvCodes: (
      <ClassificationListSelect
        classificationName={`signals`}
        options={signalCpvcodeOptions}
        value={value}
        onChange={onChange}
        isChecked={isChecked}
        allCpvCodes={allCpvCodes}
        emptyState={
          <SignalEmptyState
            entity="CPV Code"
            message={
              isRelevancySearchEnabled
                ? "Save CPV Codes via your signal settings to uncover more renewal opportunities and achieve relevant results."
                : undefined
            }
          />
        }
      />
    ),

    browseCpvCodes: (
      <CpvTreeSelect
        value={value}
        onChange={onChange}
        signalCodeIds={signalCodeIds}
        cpvTree={cpvTree}
        isLoading={isLoading}
      />
    ),

    searchCpvCodes: (
      <CpvTreeSelect
        value={value}
        onChange={onFilteredChange}
        signalCodeIds={signalCodeIds}
        cpvTree={filteredCpvCodes}
        isLoading={isLoading}
        expand={true}
      />
    ),
  };

  return (
    <div>
      {isOpen ? (
        <Dropdown
          isLoading={isLoading}
          isOpen={isOpen}
          textSearch={textSearch}
          value={value}
          description="Search the CPV Codes you want to track"
          categoryToLabel={categoryToLabel}
          categoryToContent={categoryToContent}
          textSearchCategory="searchCpvCodes"
        />
      ) : null}
    </div>
  );
}

export default CpvDropdown;
